<template>
  <v-container fluid class="login-page bg-grey-lighten-4">
    <v-card elevation="10" max-width="430" class="login-card rounded-xl">
      <div class="login-card__top">
        <v-btn text color="primary" class="top-home-link" to="/">
          <v-icon left>mdi-home-outline</v-icon>
          {{ $t('common.backHome') }}
        </v-btn>
      </div>

      <div class="login-header">
        <v-avatar size="80" class="logo-avatar mb-2">
          <v-img :src="require('@/assets/logo.png')" alt="Logo Konven.io" contain></v-img>
        </v-avatar>

        <h1 class="login-title">
          {{ $t('login.welcome') }}
        </h1>
      </div>

      <v-form @submit.prevent="handleLogin" ref="loginForm" v-model="loginValid" class="login-form">
        <v-text-field
          v-model="email"
          :label="$t('login.email')"
          type="email"
          outlined
          dense
          prepend-inner-icon="mdi-email-outline"
          :rules="[rules.required, rules.email]"
          required
        ></v-text-field>

        <v-text-field
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          :label="$t('login.password')"
          outlined
          dense
          prepend-inner-icon="mdi-lock-outline"
          :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :rules="[rules.required]"
          @click:append="showPassword = !showPassword"
          required
        ></v-text-field>

        <div class="forgot-row">
          <v-btn text small color="primary" class="forgot-button" @click="handleResetPassword">
            {{ $t('login.forgot') }}
          </v-btn>
        </div>

        <v-btn color="primary" type="submit" class="login-submit" large block depressed>
          <v-icon left>mdi-login</v-icon>
          {{ $t('login.enter') }}
        </v-btn>

        <v-btn color="red darken-1" class="white--text" @click="loginGoogle" large block depressed>
          <v-icon left>mdi-google</v-icon>
          {{ $t('login.google') }}
        </v-btn>

        <div class="login-choice-separator" aria-hidden="true">
          <span></span>
          <strong>{{ $t('common.or') }}</strong>
          <span></span>
        </div>

        <v-btn
          outlined
          color="primary"
          class="register-request-button"
          large
          block
          to="/request-access"
        >
          <v-icon left>mdi-account-plus-outline</v-icon>
          {{ $t('common.startFree') }}
        </v-btn>
      </v-form>

      <v-alert v-if="error" type="error" class="mt-5" dense border="left">
        {{ error }}
      </v-alert>

      <div class="login-contact">
        <strong>{{ $t('login.contact') }}</strong>
        <a href="mailto:valkiria.lab.apps@gmail.com" class="text-decoration-none">
          valkiria.lab.apps@gmail.com
        </a>
      </div>

      <div class="legal-links mt-2">
        <router-link to="/aviso-legal" target="_blank" rel="noopener">{{ $t('info.legalTitle') }}</router-link>
        <span aria-hidden="true">|</span>
        <router-link to="/politica-de-privacidad" target="_blank" rel="noopener">{{ $t('info.privacyTitle') }}</router-link>
        <span aria-hidden="true">|</span>
        <router-link to="/politica-de-cookies" target="_blank" rel="noopener">{{ $t('info.cookiesTitle') }}</router-link>
        <span class="legal-version">v{{ legalTermsVersion }}</span>
      </div>

      <v-snackbar v-model="snackbar" color="green darken-1" timeout="3000" top right>
        {{ snackbarText }}
      </v-snackbar>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { LEGAL_TERMS_VERSION } from '@/config/legal'

export default {
  name: 'Login',

  data: () => ({
    email: '',
    password: '',
    loginValid: false,
    showPassword: false,
    snackbar: false,
    snackbarText: ''
  }),

  computed: {
    ...mapGetters('auth', ['authError']),
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION
    },
    error() {
      return this.authError
    },
    rules() {
      return {
        required: (value) => !!value || this.$t('common.required'),
        email: (value) => /.+@.+\..+/.test(value) || this.$t('common.invalidEmail')
      }
    }
  },

  methods: {
    ...mapActions('auth', ['login', 'loginWithGoogle', 'logout', 'resetPassword', 'setAuthError']),

    // --- LOGIN CON GOOGLE ---
    async loginGoogle() {
      await this.loginWithGoogle()

      if (!this.error) {
        const role = this.$store.getters['auth/userRole']

        if (!['admin', 'superadmin'].includes(role)) {
          await this.denyNonAdminAccess()
          return
        }

        this.$router.push('/home/notices')
      }
    },

    // --- LOGIN NORMAL ---
    async handleLogin() {
      if (!this.$refs.loginForm.validate()) {
        await this.setAuthError(this.$t('login.missingCredentials'))
        return
      }

      await this.login({ email: this.email, password: this.password })

      if (!this.error) {
        const role = this.$store.getters['auth/userRole']

        if (!['admin', 'superadmin'].includes(role)) {
          await this.denyNonAdminAccess()
          return
        }

        this.$router.push('/home/notices')
      }
    },

    async handleResetPassword() {
      if (!this.email) {
        await this.setAuthError(this.$t('login.missingResetEmail'))
        return
      }

      try {
        await this.resetPassword(this.email.trim())
        this.snackbarText = this.$t('login.passwordResetSent')
        this.snackbar = true
      } catch (e) {
        this.snackbarText = this.$t('login.passwordResetError')
        this.snackbar = true
      }
    },

    async denyNonAdminAccess() {
      await this.logout()
      await this.setAuthError(this.$t('login.noPermission'))
      this.snackbarText = this.$t('login.accessUnavailable')
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.login-card {
  position: relative;
  padding: 22px 24px 24px;
  width: 100%;
}

.login-language {
  opacity: 0.72;
  position: absolute;
  right: 14px;
  top: 12px;
  transition: opacity 0.2s ease;
}

.login-language:hover {
  opacity: 1;
}

.v-card {
  animation: fadeIn 0.6s ease;
}

.login-card__top {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.login-header {
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
  text-align: center;
}

.login-title {
  color: #212121;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  margin: 6px 0 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.login-choice-separator {
  align-items: center;
  color: #8b829f;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto 1fr;
  margin: 8px 0 4px;
  text-transform: lowercase;
}

.login-choice-separator span {
  background: #ddd7ee;
  height: 1px;
}

.login-choice-separator strong {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.legal-links {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 8px;
  justify-content: center;
  text-align: center;
}

.legal-version {
  color: rgba(63, 61, 86, 0.48);
  font-weight: 700;
}

.forgot-row {
  display: flex;
  justify-content: flex-end;
  margin: -12px 0 8px;
}

.forgot-button {
  letter-spacing: 0;
  padding-right: 0;
}

.top-home-link {
  font-weight: 700;
  letter-spacing: 0;
}

.register-request-button {
  font-weight: 700;
  letter-spacing: 0;
}

.login-submit {
  font-weight: 800;
  letter-spacing: 0;
}

.login-contact {
  background: #f7f4ff;
  border: 1px solid rgba(123, 47, 247, 0.12);
  border-radius: 12px;
  color: #616161;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 20px;
  padding: 12px;
  text-align: center;
}

.logo-avatar {
  border: 2px solid #d2c5f0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

a {
  color: #3f51b5;
}

a:hover {
  text-decoration: underline;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

@media (max-width: 600px) {
  .login-page {
    align-items: flex-start;
    padding: 12px;
  }

  .login-card {
    border-radius: 16px !important;
    padding: 18px 16px 20px;
  }

  .login-title {
    font-size: 1.25rem;
  }

  .login-contact a {
    overflow-wrap: anywhere;
  }

  .legal-links {
    gap: 6px;
  }
}

</style>
